import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Nav, OverlayTrigger, Tooltip } from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import Avatar from "../components/Avatar";
import useCuration from "../apis/Curation";
import { getInfoCount, getInfoProfit } from "../utils/portfolioChart";
import Stock from "../stock/Stock";
import {
  findMatchingTicker,
  getRequestedCurationParams,
} from "./utils/curationPageUtils";

import img14 from "../assets/img/img14.jpg";

const TITLES = {
  guruSale: "투자대가 바겐세일 종목",
  fundSale: "미연기금 바겐세일 종목",
  guruTrend: "투자대가 투자 트랜드",
  fundTrend: "미연기금 투자 트랜드",
  guruMomentum: "투자대가 모멘텀",
  fundMomentum: "미연기금 모멘텀",
};

const TREND_TYPE_CONFIG = {
  sale: [
    { key: "SaleM1", label: "1개월", statusText: "보유" },
    { key: "SaleM3", label: "3개월", statusText: "보유" },
    { key: "SaleM6", label: "6개월", statusText: "보유" },
    { key: "SaleM12", label: "12개월", statusText: "보유" },
  ],
  momentum: [
    { key: "MomentumUp", label: "모멘텀 상승", statusText: "보유" },
    { key: "MomentumDown", label: "모멘텀 하락", statusText: "보유" },
  ],
  trend: [
    { key: "HoldStock", label: "최다보유", statusText: "보유" },
    { key: "BuyStock", label: "최다매수", statusText: "매수" },
    { key: "SellStock", label: "최다매도", statusText: "매도" },
    { key: "BuyallStock", label: "매수Only", statusText: "매수" },
  ]
};

const VALID_CUR_TYPES = ["guruSale", "fundSale", "guruTrend", "fundTrend", "guruMomentum", "fundMomentum"];
const TREND_LOAD_SIZE = 20;

function getTrendMode(curType) {
  if (curType === "guruSale" || curType === "fundSale") {
    return "sale";
  }
  if (curType === "guruMomentum" || curType === "fundMomentum") {
    return "momentum";
  }
  return "trend";
}

function buildTrendTypes(curType) {
  const isGuruType = curType.startsWith("guru");
  const prefix = isGuruType ? "guru" : "fund";
  const mode = getTrendMode(curType);

  return TREND_TYPE_CONFIG[mode].map((item) => ({
    key: `${prefix}${item.key}`,
    label: item.label,
    statusText: item.statusText,
  }));
}

export default function Curations() {
  const { curtype } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const activeCurType = VALID_CUR_TYPES.includes(curtype) ? curtype : "guruSale";
  const trendMode = getTrendMode(activeCurType);
  const isSaleType = trendMode === "sale";
  const isMomentumType = trendMode === "momentum";
  const { requestedSeltype, requestedTicker } = useMemo(
    () => getRequestedCurationParams(location.search),
    [location.search]
  );

  useEffect(() => {
    document.body.classList.add('page-app')
    return () => {
      document.body.classList.remove('page-app')
    }
  }, []);

  // 모바일 여부 감지
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 991.98px)");
    const handleChange = () => setIsMobile(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const { data: curationItems = [], isError, isLoading } = useCuration();
  const [visibleCount, setVisibleCount] = useState(TREND_LOAD_SIZE);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState("list");
  const curationError = isError ? "트렌드 데이터를 불러오지 못했습니다." : "";
  const trendTypes = useMemo(() => buildTrendTypes(activeCurType), [activeCurType]);
  const activeKey = useMemo(() => {
    const requestedTrend = trendTypes.find((trend) => trend.key === requestedSeltype);
    return requestedTrend?.key || trendTypes[0]?.key || "";
  }, [trendTypes, requestedSeltype]);

  useEffect(() => {
    setVisibleCount(TREND_LOAD_SIZE);
  }, [activeKey]);

  const trendLists = useMemo(() => {
    const map = {};
    trendTypes.forEach((type) => {
      map[type.key] = [];
    });

    curationItems.forEach((item) => {
      const key = item.seltype;
      if (map[key]) {
        map[key].push(item);
      }
    });

    Object.keys(map).forEach((key) => {
      map[key] = map[key]
        .slice()
        .sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))
    });

    return map;
  }, [curationItems, trendTypes]);

  const activeTrendItems = useMemo(() => {
    return (trendLists[activeKey] || []).map((item) => ({
      ...item,
      infoCount: getInfoCount(item.info),
      infoProfit: getInfoProfit(item.info),
    }));
  }, [trendLists, activeKey]);

  const formatDiscount = (value) => {
    if (!Number.isFinite(value)) return null;
    const normalized = Math.abs(value) <= 1 ? Math.abs(value) * 100 : Math.abs(value);
    const rounded = Math.round(normalized * 10) / 10;
    const display = rounded.toFixed(1);
    return `${display}%`;
  };

  const visibleTrendItems = useMemo(() => {
    return activeTrendItems.slice(0, visibleCount);
  }, [activeTrendItems, visibleCount]);

  const hasMoreTrendItems = visibleTrendItems.length < activeTrendItems.length;
  
  const requestedTrendTicker = useMemo(() => {
    return findMatchingTicker(activeTrendItems, requestedTicker);
  }, [activeTrendItems, requestedTicker]);

  const selectedTicker = useMemo(() => {
    return requestedTrendTicker || visibleTrendItems[0]?.ticker || null;
  }, [requestedTrendTicker, visibleTrendItems]);

  const activeTrend = useMemo(() => {
    return trendTypes.find((trend) => trend.key === activeKey) || null;
  }, [trendTypes, activeKey]);

  const showDetailPane = !isMobile || mobileView === "detail";

  const updateSearchParams = useCallback((updates) => {
    const searchParams = new URLSearchParams(location.search);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        searchParams.delete(key);
        return;
      }
      searchParams.set(key, value);
    });

    const nextSearch = searchParams.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`);
  }, [location.pathname, location.search, navigate]);

  const loadMoreTrendItems = useCallback(() => {
    if (!hasMoreTrendItems) return;
    setVisibleCount((prev) => Math.min(prev + TREND_LOAD_SIZE, activeTrendItems.length));
  }, [hasMoreTrendItems, activeTrendItems.length]);

  useEffect(() => {
    if (!requestedTicker) return;

    const targetIndex = activeTrendItems.findIndex((item) => {
      return String(item?.ticker || "").trim().toUpperCase() === requestedTicker;
    });
    if (targetIndex < 0) return;

    const requiredCount = Math.ceil((targetIndex + 1) / TREND_LOAD_SIZE) * TREND_LOAD_SIZE;
    setVisibleCount((prev) => Math.max(prev, Math.min(requiredCount, activeTrendItems.length)));
  }, [activeTrendItems, requestedTicker]);

  useEffect(() => {
    if (!isMobile) {
      setMobileView("list");
      return;
    }
    setMobileView(requestedTrendTicker ? "detail" : "list");
  }, [isMobile, requestedTrendTicker]);

  const sidebarStyle = isMobile
    ? { position: "relative", height: "calc(100vh)" }
    : undefined;
  return (
    <React.Fragment>
      <div className="h-100"> {/* Added d-flex to ensure chat-panel can take full height */}
        <div className={"chat-panel curation-chat-panel" + (showDetailPane ? " msg-show" : "")} style={{ minHeight: "100vh" }}> {/* Added d-flex to make chat-panel a flex container */}
          <div className="chat-sidebar" style={sidebarStyle}>
            <div className="sidebar-header">
              <span className="sidebar-title me-auto">{TITLES[activeCurType] || "Chat Messages"}</span>
            </div>
            {isLoading && activeTrendItems.length === 0 ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : curationError ? (
              <div className="text-center py-4 text-secondary">{curationError}</div>
            ) : activeTrendItems.length > 0 ? (
              <PerfectScrollbar className="sidebar-body" onYReachEnd={loadMoreTrendItems}> {/* Make scrollbar content take remaining height */}
                <label className="sidebar-label mb-2">Trend</label>
                <div className="d-flex flex-wrap gap-2 mb-4 trend-tag-row nav-scroll-hide">
                  {trendTypes.map((trend) => (
                    <button
                      key={trend.key}
                      type="button"
                      className={
                        "badge rounded-pill py-2 trend-tag" +
                        (isSaleType && trend.key.endsWith("M12")
                          ? " px-2 trend-tag-sale-m12"
                          : " px-3") +
                        (activeKey === trend.key ? " active" : "")
                      }
                      onClick={() => updateSearchParams({ seltype: trend.key, ticker: null })}
                    >
                      {trend.label}
                    </button>
                  ))}
                </div>

                <div className="d-flex align-items-center gap-1 mb-2">
                  <label className="sidebar-label mb-0">순위</label>
                  {isSaleType && (
                    <OverlayTrigger
                      placement="right"
                      overlay={(
                        <Tooltip>
                          표시된 퍼센테이지는 바겐세일 할인율(추정)입니다. 값이 클수록 할인 폭이 큽니다.
                        </Tooltip>
                      )}
                    >
                      <button type="button" className="btn btn-link p-0 text-secondary">
                        <i className="ri-question-line"></i>
                      </button>
                    </OverlayTrigger>
                  )}
                </div>
                <div className="chat-group">

                  <div className="d-flex flex-column">
                    {visibleTrendItems.map((item) => (
                      <div
                        key={`${item.seltype}-${item.ticker}-${item.rank}`}
                        className="d-flex align-items-center justify-content-between py-2 trend-list-item"
                        style={{ borderBottom: "1px solid rgba(0,0,0,0.05)", cursor: item.ticker ? "pointer" : "default" }}
                        onClick={() => {
                          if (!item.ticker) return;
                          updateSearchParams({ seltype: activeKey, ticker: item.ticker });
                          setMobileView("detail");
                        }}
                      >
                        <div className="d-flex align-items-center trend-list-main">
                          <div className="me-3">
                            <span className="badge bg-primary-subtle text-primary fs-xs">#{item.rank}</span>
                          </div>
                          <div className="trend-list-text">
                            <div className="fw-semibold trend-list-name">{item.companyKor || item.company}</div>
                            <div className="text-secondary fs-xs trend-list-subtitle">{item.nickname}</div>
                          </div>
                        </div>
                        <div className="text-end trend-list-meta d-flex align-items-center justify-content-end gap-1">
                          {isSaleType ? (
                            <span className="text-secondary fs-xs">Sale</span>
                          ) : isMomentumType ? (
                            <span className="text-secondary fs-xs">모멘텀</span>
                          ) : (
                            null
                          )}
                          <div style={isMomentumType ? { fontSize: "0.8rem" } : {}} className={`fw-semibold trend-list-value mb-0${(isSaleType && Number.isFinite(item.infoProfit)) || (isMomentumType && activeKey.endsWith('Up') && Number.isFinite(item.infoProfit)) ? ' text-danger' : ''
                            }${(isMomentumType && activeKey.endsWith('Down') && Number.isFinite(item.infoProfit)) ? ' text-primary' : ''
                            }`}>
                            {isSaleType && Number.isFinite(item.infoProfit)
                              ? `${formatDiscount(item.infoProfit)}`
                              : isMomentumType && Number.isFinite(item.infoProfit)
                                ? `${activeKey.endsWith('Up') ? '+' : ''}${item.infoProfit.toFixed(2)}%`
                                : `${item.infoCount}명 ${activeTrend?.statusText || "보유"}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              </PerfectScrollbar>
            ) : (
              <div className="text-center py-4 text-secondary">데이터가 없습니다</div>
            )}
          </div>
          <div className="chat-body">
            <div className="chat-body-header">
              <div className="chat-item">
                <Avatar img={img14} status="online" />
                <div className="chat-item-body">
                  <span className="chat-item-title mb-1">{TITLES[activeCurType] || "Trend"}</span>
                  <span>상위 종목</span>
                </div>
              </div>
              <Nav className="nav-icon ms-auto">
                <Nav.Link
                  href=""
                  className="d-lg-none"
                  onClick={(event) => {
                    event.preventDefault();
                    updateSearchParams({ ticker: null });
                    setMobileView("list");
                  }}
                >
                  <i className="ri-arrow-left-s-line" style={{fontSize: "30px"}}></i>
                </Nav.Link>
              </Nav>
            </div>
            <div className="chat-body-content curation-body-content">
              <div className="p-2">
                {selectedTicker ? (
                  <div className="stock-content-scroll">
                    <Stock ticker={selectedTicker} embedded />
                  </div>
                ) : (
                  <div className="text-secondary py-4 text-center">
                    종목을 선택하면 차트가 표시됩니다.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </React.Fragment>
  );
}
